import React, { createContext, useContext, useEffect, useState } from "react";

export interface User {
  name: string;
  email: string;
  gender: string;
  salutation: string;
  title: string;
  street: string;
  postal_code: string;
  city: string;
  phone: string;
  stripe_customer_id?: string; // wichtig für Stripe-Flow
  is_staff?: boolean;
  is_admin?: boolean;
  is_admin_user?: boolean;
  subscription?: {
    stripe_subscription_id: string;
    plan_name: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
    billing_interval: string;
    cancel_at_period_end: boolean;
    cancel_requested_at: string | null;
    canceled_at: string | null;
    cancellation_policy: string;
    start_date: string;
    price_display?: string;
    status_display?: string;
    currency?: string;
    cancellation_pending?: boolean;
    is_virtual_fallback?: boolean;
    message?: string;
    display_status?: string;
  };
}

interface UserContextType {
  user: User | null;
  loading: boolean;
  refreshUser: () => void;
  updateUser: (updates: Partial<User>) => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  refreshUser: () => { },
  updateUser: async () => { },
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = () => {
    setLoading(true);
    fetch("/auth/user/user-profile/", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          console.error("Fehlerhafte Antwort:", text);
          return;
        }
        const data = await res.json();

        
        const safe: User = {
          name: data.name || "",
          email: data.email || "",
          gender: data.gender || "",
          salutation: data.salutation || "",
          title: data.title || "",
          street: data.street || "",
          postal_code: data.postal_code || "",
          city: data.city || "",
          phone: data.phone || "",
          stripe_customer_id: data.stripe_customer_id || "", // ACHTUNG: falls dein API das zurückgibt
          is_admin_user: data.is_admin_user || false,
          subscription: data.subscription ? {
            stripe_subscription_id: data.subscription.stripe_subscription_id,
            plan_name: data.subscription.plan_name,
            status: data.subscription.status,
            current_period_start: data.subscription.current_period_start,
            current_period_end: data.subscription.current_period_end,
            billing_interval: data.subscription.billing_interval,
            price_display: data.subscription.price_display,
            cancel_at_period_end: data.subscription.cancel_at_period_end,
            cancel_requested_at: data.subscription.cancel_requested_at,
            canceled_at: data.subscription.canceled_at,
            cancellation_policy: data.subscription.cancellation_policy,
            status_display: data.subscription.status_display,
            currency: data.subscription.currency,
            start_date: data.subscription.start_date,
            cancellation_pending: data.subscription.cancellation_pending,
            is_virtual_fallback: data.subscription.is_virtual_fallback,
            message: data.subscription.message,
            display_status: data.subscription.display_status,
          } : undefined,
        };
        setUser(safe);
      })
      .catch((err) => console.error("Profil-Request fehlgeschlagen:", err))
      .finally(() => setLoading(false));
  };

  const updateUser = async (updates: Partial<User>) => {
    await fetch("/auth/user/user-profile/", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(updates),
    });
    await fetchUser();
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <UserContext.Provider value={{ user, loading, refreshUser: fetchUser, updateUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);